import { z } from "zod";

/**
 * Schéma d'une entrée éditoriale (CNT-002).
 *
 * Extension assumée du schéma minimal du prompt maître : `draftedBy` et
 * `reviewedBy` sont ajoutés pour rendre applicable la règle « l'IA ne publie
 * jamais seule » (LEG-009, EDT-009, EDT-016). Sans ces champs, la règle ne
 * serait qu'une intention.
 */

export const EDITORIAL_STATUSES = ["playtest", "approved"] as const;

export const contentEntrySchema = z.object({
  id: z.string().min(1),
  locale: z.literal("fr"),
  /** Orthographe exacte, accentuée, avec ses tirets, apostrophes et espaces. */
  answer: z.string().min(1),
  /** Variantes supplémentaires acceptées. Doivent avoir la même longueur normalisée. */
  acceptedAnswers: z.array(z.string()),
  definition: z.string().min(1),
  /**
   * Étymologie, anecdote, exemple d'emploi ou origine d'expression.
   * Facultatif : `null` plutôt qu'une anecdote forcée. Affiché au résultat seulement.
   */
  culturalNote: z.string().min(1).nullable().optional(),
  /**
   * Ordre officiel de révélation, indices sur la réponse normalisée.
   * Facultatif : à défaut, le moteur l'engendre depuis l'identifiant du défi.
   */
  revealOrder: z.array(z.number().int().nonnegative()).optional(),
  difficulty: z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
  ]),
  editorialStatus: z.enum(EDITORIAL_STATUSES),
  /** Confirmation humaine que la formulation publiée est originale (EDT-010). */
  originalityConfirmed: z.boolean(),
  draftedBy: z.enum(["human", "ai_assisted"]),
  /** Identifiant du relecteur, distinct de l'auteur. `null` tant qu'aucune relecture n'a eu lieu. */
  reviewedBy: z.string().min(1).nullable(),
});

export type ContentEntry = z.infer<typeof contentEntrySchema>;

export const contentFileSchema = z.object({
  schemaVersion: z.literal(1),
  locale: z.literal("fr"),
  /** Avertissement de provenance, obligatoire sur un corpus non validé. */
  notice: z.string().optional(),
  entries: z.array(contentEntrySchema),
});

export type ContentFile = z.infer<typeof contentFileSchema>;
